import { EntityTypes, RichEditorVariable } from '../types';
import { RawDraftContentState } from 'draft-js';
import { Remarkable } from 'remarkable';
import { findVariable, getVariableRegex, getWrappedVariableRegex } from '../utils';
import { markdownToDraft } from 'markdown-draft-js';

interface VariableToken extends Remarkable.ContentToken {
    variable: string;
}

export enum ParserTokens {
    VariableOpen = 'variable_open',
    VariableClose = 'variable_close',
    LinkOpen = 'link_open',
}

export const convertMarkdownToDraft = (markdown: string, variables?: RichEditorVariable[]): RawDraftContentState => {
    return markdownToDraft(markdown, {
        preserveNewlines: true,
        remarkablePlugins: [
            variableParserPlugin(variables),
            // Same plugin but with the regex for the <% variable %> format
            variableParserPlugin(variables, getWrappedVariableRegex()),
        ],
        blockEntities: {
            // This converts a VariableOpen token into a `VARIABLE` entity
            [ParserTokens.VariableOpen]: (item: VariableToken) => {
                const foundVar = findVariable(item.variable, variables);
                return {
                    type: EntityTypes.Variable,
                    mutability: 'IMMUTABLE',
                    data: foundVar,
                };
            },
        },
    });
};

/**
 * In order to properly convert markdown syntax to DraftJS raw state and support variables, we're
 * using `markdown-draft-js`. It supports creating custom plugins to parse text into custom entities.
 * This is a custom `remarkable` plugin (`markdown-draft-js` uses `remarkable` under the hood) to
 * convert both `$variable` and `<% variable %>` into a DraftJS `VARIABLE` entity.
 */
const variableParserPlugin =
    (variables?: RichEditorVariable[], regex: RegExp = getVariableRegex()) =>
    (md: Remarkable) => {
        md.inline.ruler.push('variable', variableParser(variables, regex), {});
    };

/**
 * Parser that tokenizes the string to extract variables from it.
 */
const variableParser: (variables?: RichEditorVariable[], regex?: RegExp) => Remarkable.InlineParsingRule =
    (variables, regex = getVariableRegex()) =>
    (state) => {
        let found = false;
        const blockText = state.src;
        regex.lastIndex = state.pos;

        let matchArr;
        while ((matchArr = regex.exec(blockText)) !== null) {
            if (matchArr) {
                const match = matchArr[1];
                const fullMatch = matchArr[0];
                if (match && fullMatch) {
                    const foundVar = findVariable(match, variables);

                    if (foundVar) {
                        if (state.pos < matchArr.index) {
                            state.push({
                                type: 'text',
                                level: state.level,
                                content: blockText.slice(state.pos, matchArr.index),
                            });
                            state.pos = matchArr.index;
                        }
                        state.push({
                            type: ParserTokens.VariableOpen,
                            level: state.level,
                            variable: Array.isArray(foundVar.name) ? foundVar.name[0] : foundVar.name,
                        } as VariableToken);

                        state.push({
                            type: 'text',
                            level: state.level,
                            content:
                                foundVar.displayText ??
                                (Array.isArray(foundVar.name) ? foundVar.name[0] : foundVar.name),
                        });

                        state.push({
                            type: ParserTokens.VariableClose,
                            level: state.level,
                        });

                        found = true;
                        state.pos = state.pos + fullMatch.length;
                    }
                }
            }
        }
        return found;
    };
